import type {
  DisableOpsUserResponse,
  EnableOpsUserResponse,
  ListOpsUsersResponse,
  OpsUserRecord,
  ResetOpsUserPasswordResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/ops-users';
const OPS_USERS_KEY = ['ops-users'] as const;

export async function listOpsUsers(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsUserRecord[]> {
  const response = await opsApiRequest<ListOpsUsersResponse>(BASE, { signal }, fetchImpl);
  return response.users;
}

export async function disableOpsUser(
  id: string,
  fetchImpl: typeof fetch,
): Promise<OpsUserRecord> {
  const response = await opsApiRequest<DisableOpsUserResponse>(
    `${BASE}/${encodeURIComponent(id)}/disable`,
    { method: 'POST' },
    fetchImpl,
  );
  return response.user;
}

export async function enableOpsUser(
  id: string,
  fetchImpl: typeof fetch,
): Promise<OpsUserRecord> {
  const response = await opsApiRequest<EnableOpsUserResponse>(
    `${BASE}/${encodeURIComponent(id)}/enable`,
    { method: 'POST' },
    fetchImpl,
  );
  return response.user;
}

export async function resetOpsUserPassword(
  id: string,
  fetchImpl: typeof fetch,
): Promise<ResetOpsUserPasswordResponse> {
  return opsApiRequest<ResetOpsUserPasswordResponse>(
    `${BASE}/${encodeURIComponent(id)}/reset-password`,
    { method: 'POST' },
    fetchImpl,
  );
}

export function useOpsUsers(): UseQueryResult<OpsUserRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: OPS_USERS_KEY,
    queryFn: ({ signal }) => listOpsUsers(fetchImpl, signal),
  });
}

export function useDisableOpsUser(): UseMutationResult<OpsUserRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => disableOpsUser(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}

export function useEnableOpsUser(): UseMutationResult<OpsUserRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => enableOpsUser(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}

export function useResetOpsUserPassword(): UseMutationResult<
  ResetOpsUserPasswordResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => resetOpsUserPassword(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}
